import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { getStorageItem, removeStorageItem, safeJsonParse, setStorageItem, type STORAGE_EVENT } from '@utils'
import { useEffect, useMemo, useSyncExternalStore, type SetStateAction } from 'react'

const getServerSnapshot = () => {
  throw Error('useStorage is a client-only hook')
}

export function useStorage<S>(key: string, initialValue: S, type: STORAGE_EVENT) {
  // Set initial value to storage
  useEffect(() => {
    if (
      getStorageItem(key, type) === null
      && typeof initialValue !== 'undefined'
    ) {
      setStorageItem(key, initialValue, type)
    }
  }, [key, initialValue, type])

  const subscribe = (onStoreChange: () => void) => {
    window.addEventListener(type, onStoreChange)
    return () => window.removeEventListener(type, onStoreChange)
  }

  const getSnapshot = () => getStorageItem(key, type)

  const store = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  )

  const setState = useCallbackEvent(
    (value: SetStateAction<S>) => {
      try {
        const latestStore = getStorageItem(key, type)
        if (latestStore === null) {
          return
        }

        const prevState = safeJsonParse<S, S>(latestStore, initialValue)
        const nextState = value instanceof Function ? value(prevState) : value

        if (nextState === undefined || nextState === null) {
          removeStorageItem(key, type)
        }
        else {
          setStorageItem(key, nextState, type)
        }
      }
      catch (e) {
        console.warn(e)
      }
    },
  )

  const currentState = useMemo(() => store ? safeJsonParse<S, S>(store, initialValue) : initialValue, [store, initialValue])

  return [currentState, setState] as const
}
